import { Outlet, NavLink, Link } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Globe, ArrowUpRight } from "lucide-react";
import { COMPANY, LOGO_URL } from "@/lib/constants";
import { useI18n, LANGS } from "@/lib/i18n";
import Chatbot from "@/components/Chatbot";
import Footer from "@/components/Footer";

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { t, lang, setLang } = useI18n();
  const NAV = [
    { to: "/", label: t("nav.home") },
    { to: "/usluge", label: t("nav.services") },
    { to: "/reference", label: t("nav.references") },
    { to: "/o-nama", label: t("nav.about") },
    { to: "/kontakt", label: t("nav.contact") },
  ];
  const currentLang = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0A0A] text-white relative z-0">
      <header
        className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/5"
        data-testid="site-header"
      >
        <div className="dsvm-container flex items-center justify-between h-20 gap-6">
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0" data-testid="brand-logo">
            <img src={LOGO_URL} alt="DSVM Group" className="h-12 w-auto" />
            <div className="hidden sm:block font-heading">
              <div className="dsvm-label text-[10px]">Electrical · Security · Data</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-9 flex-1 justify-center">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `relative text-[13px] font-medium tracking-wide transition-colors py-2 ${
                    isActive ? "text-[#C5A059]" : "text-white/70 hover:text-white"
                  } after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[1px] after:bg-[#C5A059] after:transition-all ${
                    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            {/* Compact language switcher — current lang + dropdown */}
            <div className="relative" data-testid="lang-switcher">
              <button
                onClick={() => setLangOpen(!langOpen)}
                onBlur={() => setTimeout(() => setLangOpen(false), 150)}
                className="flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-white border border-white/10 hover:border-[#C5A059]/40 transition-colors"
                data-testid="lang-current"
              >
                <Globe className="w-3.5 h-3.5" />
                {currentLang.label}
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 bg-[#0A0A0A] border border-white/10 shadow-2xl z-50">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      onMouseDown={(e) => { e.preventDefault(); setLang(l.code); setLangOpen(false); }}
                      className={`block w-full text-left px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] transition-colors whitespace-nowrap ${
                        lang === l.code ? "text-[#C5A059] bg-white/5" : "text-white/60 hover:text-white hover:bg-white/5"
                      }`}
                      data-testid={`lang-${l.code}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link to="/kontakt" className="dsvm-btn-cta" data-testid="header-cta">
              <span>{t("cta.request_quote")}</span>
              <span className="dsvm-btn-cta-arrow">
                <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
              </span>
            </Link>
          </div>

          <button
            className="lg:hidden text-white"
            onClick={() => setOpen(!open)}
            aria-label="menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/10 bg-[#0A0A0A]" data-testid="mobile-menu">
            <div className="dsvm-container py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `text-base font-medium ${isActive ? "text-[#C5A059]" : "text-white/80"}`
                  }
                  data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-2 pt-2">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] transition-colors border ${
                      lang === l.code ? "bg-[#C5A059] text-black border-[#C5A059]" : "text-white/60 border-white/10"
                    }`}
                    data-testid={`mobile-lang-${l.code}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <Link
                to="/kontakt"
                onClick={() => setOpen(false)}
                className="dsvm-btn-cta mt-2 justify-center"
                data-testid="mobile-cta"
              >
                <span>{t("cta.request_quote")}</span>
                <span className="dsvm-btn-cta-arrow">
                  <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
                </span>
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 relative z-10">
        <Outlet />
      </main>

      <Footer />
      <Chatbot />
    </div>
  );
}
